import { Phone, Lock, Printer, FileDown, ExternalLink } from "lucide-react";
import { usePageTitle } from "@/hooks/usePageTitle";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";
import DownloadButton from "@/components/DownloadButton";
import ProtectedDownload from "@/components/ProtectedDownload";
import { Reveal } from "@/components/Reveal";
import { FlowerDoodle, WavyUnderline } from "@/components/Doodles";

const OCFS_FORMS_LINK = "https://ocfs.ny.gov/programs/childcare/regulations/";

const FORMS = [
    { id: "forms-cover", name: "Start Here: Cover & Intake Checklist", desc: "Welcome plus everything due before day one, in one place", signer: "none", required: "Every child", file: "forms-cover.pdf" },
    { id: "nys-blue-card", name: "NYS Blue Card (OCFS-LDSS-0792)", desc: "Official OCFS day care enrollment card", signer: "parent", required: "Every child", official: "https://ocfs.ny.gov/forms/ldss/ldss-0792/OCFS-LDSS-0792.pdf" },
    { id: "forms-immunization", name: "Attachment 1: Immunization Requirements & Record", desc: "Shot requirements and record — or bring a NYSIIS printout", signer: "doctor", required: "Every child", file: "forms-immunization.pdf" },
    { id: "ocfs-medical-statement", name: "OCFS Child in Care Medical Statement (OCFS-LDSS-4433)", desc: "Doctor's exam from the past 12 months, including lead screening at ages 1 and 2", signer: "doctor", required: "Every child", official: "/docs/OCFS-LDSS-4433.docx", officialLabel: "Download form (Word)" },
    { id: "form-a", name: "Form A: Child Enrollment / Registration", desc: "Family and child information", signer: "parent", required: "Every child", file: "form-a.pdf" },
    { id: "form-b", name: "Form B: Emergency Contacts, Authorized Pick-Up & Custody", desc: "Who can pick up; attach any custody papers or court orders", signer: "parent", required: "Every child", file: "form-b.pdf" },
    { id: "form-c", name: "Form C: Child Health Information & Emergency Treatment Consent", desc: "Health history and emergency treatment consent", signer: "parent", required: "Every child", file: "form-c.pdf" },
    { id: "form-d", name: "Form D: Allergy & Special Health Care Needs Plan", desc: "Allergy and care plan; emergency medicine must be on site", signer: "parent+doctor", ifNeeded: true, file: "form-d.pdf" },
    { id: "form-e", name: "Form E: Medication & Topical Product Consent", desc: "Sunscreen, creams, and bug spray consent", signer: "parent", required: "Every child", file: "form-e.pdf" },
    { id: "ocfs-medication-consent", name: "OCFS Medication Consent (OCFS-LDSS-7002)", desc: "Official form for emergency medicine (EpiPen, inhaler)", signer: "parent+doctor", ifNeeded: true, official: "/docs/OCFS-LDSS-7002.docx", officialLabel: "Download form (Word)", esLink: "https://ocfs.ny.gov/forms/?find=ldss-7002" },
    { id: "form-f", name: "Form F: Infant / Toddler Care Plan", desc: "Feeding, sleep, and diaper routines", signer: "parent", required: "Children under 3", file: "form-f.pdf" },
    { id: "form-g", name: "Form G: Activity, Photo & Media Permissions", desc: "Photos, walks, water play, and media", signer: "parent", required: "Every child", file: "form-g.pdf" },
    { id: "form-h", name: "Form H: Tuition & Fee Agreement", desc: "Rates, payment rules, and deposit", signer: "parent+asia", required: "Every child", file: "form-h.pdf" },
    { id: "form-i", name: "Form I: Handbook Acknowledgment", desc: "Confirms you read the Parent Handbook", signer: "parent+asia", required: "Every child", file: "form-i.pdf" },
    { id: "form-j", name: "Form J: Rest Time & Napping Agreement", desc: "Rest time routine and safe-sleep agreement", signer: "parent+asia", required: "Every child", file: "form-j.pdf" },
    { id: "form-k", name: "Form K: Behavior Guidance & Aggressive Behavior Policy", desc: "Our behavior guidance approach and policy", signer: "parent+asia", required: "Every child", file: "form-k.pdf" },
];

const Tag = ({ tone, children }) => {
    const tones = {
        green: "bg-sage/20 text-sageink",
        asia: "bg-sun/50 text-soil",
        coral: "bg-coral/15 text-coraldeep",
        yellow: "bg-sun/30 text-soil",
    };
    return <span className={`text-[11px] font-extrabold uppercase tracking-wide px-2.5 py-1 rounded-full ${tones[tone]}`}>{children}</span>;
};

const SignerTags = ({ signer, ifNeeded }) => (
    <div className="flex flex-wrap gap-1.5">
        {(signer === "parent" || signer.startsWith("parent+")) && <Tag tone="green">Parent signs</Tag>}
        {signer === "parent+asia" && <Tag tone="asia">Asia signs</Tag>}
        {(signer === "doctor" || signer === "parent+doctor") && <Tag tone="coral">Doctor signs</Tag>}
        {ifNeeded && <Tag tone="yellow">If needed</Tag>}
    </div>
);

export default function Forms() {
    usePageTitle("OCFS Enrollment Forms Checklist | Little Seeds Flourish");

    return (
        <div>
            <PageHero
                eyebrow="Enroll → Enrollment forms"
                title={<>Getting Ready for Day One</>}
                sub="Welcome to the Little Seeds family! New York State (OCFS) requires the forms below before your child can start. Download each one, or complete them together at your enrollment visit."
                doodle="bee"
            />

            <section className="pb-14">
                <div className="max-w-4xl mx-auto px-5 sm:px-8">
                    <Reveal>
                        <div
                            className="mb-8 bg-skysoft border-2 border-sky rounded-3xl p-5 flex gap-3.5 items-start"
                            data-testid="forms-password-note"
                        >
                            <Lock size={20} className="text-sageink shrink-0 mt-0.5" />
                            <p className="text-sm text-soil leading-relaxed">
                                <b>Private downloads:</b> the forms contain family information, so they're password-protected —
                                Asia shares the password after your tour. The three official OCFS forms are open to everyone.
                            </p>
                        </div>
                    </Reveal>
                    <Reveal>
                        <p className="text-lg text-soil font-bold leading-relaxed mb-8">
                            Questions? Call or text Asia at{" "}
                            <a href="tel:+15163243418" data-testid="forms-phone-link" className="font-extrabold text-sageink underline underline-offset-4">
                                516-324-3418
                            </a>
                            .
                        </p>
                    </Reveal>
                    <div className="space-y-4" data-testid="forms-checklist">
                        {FORMS.map((f, i) => (
                            <Reveal key={f.id} delay={Math.min(i * 0.04, 0.3)}>
                                <div
                                    className="bg-white border-2 border-soil/10 rounded-3xl p-5 md:p-6 flex flex-col sm:flex-row sm:items-center gap-4 hover-lift"
                                    data-testid={`form-card-${f.id}`}
                                >
                                    <span className="hidden md:flex p-2.5 rounded-xl bg-creamsoft border-2 border-soil/10 shrink-0 text-sageink">
                                        <FileDown size={20} />
                                    </span>
                                    <div className="flex-1">
                                        <h2 className="font-display font-semibold text-lg leading-snug">{f.name}</h2>
                                        <p className="text-soilmuted text-sm mt-1">{f.desc}</p>
                                        <div className="flex flex-wrap items-center gap-2 mt-2.5">
                                            <SignerTags signer={f.signer} ifNeeded={f.ifNeeded} />
                                            {f.required !== "Only if needed" && (
                                                <span className="text-[11px] font-bold text-soil/45 uppercase tracking-wide">Required for: {f.required}</span>
                                            )}
                                        </div>
                                        {f.esLink && (
                                            <a
                                                href={f.esLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-testid={`form-es-link-${f.id}`}
                                                className="inline-flex items-center gap-1 mt-2.5 text-sm font-extrabold text-sageink underline underline-offset-4 hover:text-coraldeep transition-colors"
                                            >
                                                En español <ExternalLink size={13} />
                                            </a>
                                        )}
                                    </div>
                                    {f.official ? (
                                        <DownloadButton href={f.official} label={f.officialLabel || "Official OCFS PDF"} testId={`form-download-${f.id}`} />
                                    ) : (
                                        <ProtectedDownload file={f.file} label="Download PDF" testId={`form-download-${f.id}`} />
                                    )}
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal>
                        <div className="mt-8 bg-sun/25 border-2 border-sun rounded-3xl p-6" data-testid="forms-deposit-note">
                            <p className="font-bold text-soil text-lg">
                                Also due before day one: a security deposit equal to one week of your child's contracted
                                weekly tuition, and the first week's tuition. No registration fee.
                            </p>
                        </div>
                    </Reveal>

                    <div className="mt-10 grid md:grid-cols-2 gap-5">
                        <Reveal>
                            <div className="bg-creamsoft border-2 border-soil/10 rounded-3xl p-6 h-full" data-testid="forms-return-card">
                                <span className="inline-flex p-2.5 rounded-xl bg-white border-2 border-soil/10 mb-3">
                                    <Printer size={20} className="text-sageink" />
                                </span>
                                <h2 className="font-display font-semibold text-xl mb-2">How to return forms</h2>
                                <p className="text-soilmuted text-sm leading-relaxed">
                                    Print, sign, and bring your forms to the enrollment visit — that's the easiest way for
                                    everyone. Families review and update forms at least once a year, or whenever something changes.
                                </p>
                            </div>
                        </Reveal>
                        <Reveal delay={0.08}>
                            <div className="bg-skysoft border-2 border-sky rounded-3xl p-6 h-full" data-testid="forms-privacy-card">
                                <span className="inline-flex p-2.5 rounded-xl bg-white mb-3">
                                    <Lock size={20} className="text-coraldeep" />
                                </span>
                                <h2 className="font-display font-semibold text-xl mb-2">A note on privacy</h2>
                                <p className="text-soilmuted text-sm leading-relaxed">
                                    Please don't send medical records, immunizations, or custody papers by email or website form —
                                    they contain private health information. Bring them to your visit instead.
                                </p>
                                <p className="text-soilmuted text-sm leading-relaxed mt-3">
                                    How we collect, protect, and share your family's information — including our text messaging
                                    terms — is in our Privacy Policy &amp; Terms. Because it includes our home address, it is
                                    password protected like the forms.
                                </p>
                                <div className="mt-4">
                                    <ProtectedDownload file="privacy-policy.pdf" label="Read Privacy Policy & Terms" testId="forms-privacy-policy-download" />
                                </div>
                            </div>
                        </Reveal>
                    </div>

                    <Reveal>
                        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 text-center" data-testid="forms-ocfs-links">
                            <a
                                href={OCFS_FORMS_LINK}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="forms-ocfs-forms-link"
                                className="inline-flex items-center gap-2 font-extrabold text-sageink underline underline-offset-4 hover:text-soil transition-colors"
                            >
                                OCFS Child Care Forms & Regulations <ExternalLink size={15} />
                            </a>
                            <span className="hidden sm:inline text-soil/30">•</span>
                            <span className="text-sm text-soil/55 inline-flex items-center gap-1.5">
                                <Phone size={14} /> 516-324-3418
                            </span>
                        </div>
                    </Reveal>
                </div>
            </section>

            <CTABand title="Forms in hand? Come meet us." />
        </div>
    );
}
